import { SerializedError } from '@reduxjs/toolkit'
import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { notify } from 'components/extend/Notification/NotificationEmitter'
import { IApiMessage, IErrors } from 'components/types/app'
import { getEnv } from 'helpers/common'
import { deleteCookie, getCookie } from 'modules/login/helpers/cookie/cookie'
import { authActions } from 'src/store/auth'
import { TypeRootState } from 'store/store'
import { setClientVersion, setServerVersion } from 'store/version.slice'

import { checkRedirect } from './helpers'

export const isRTKQErrors = (errorObj: unknown): errorObj is NonNullable<{ errors?: IErrors; status?: number }> =>
  Boolean(errorObj && typeof errorObj === 'object' && 'errors' in errorObj)

export const collectApiErrors = <Data extends { message?: IApiMessage[] }>(data?: Data, status?: number | string) => {
  const errors =
    data?.message?.reduce((acc, message) => {
      if (message.path) {
        acc[message.path] = message
      }

      return acc
    }, {} as IErrors) || ({} as IErrors)

  if (status === 403) {
    errors.httpStatus = { title: 'Нет доступа' }
  }

  return { errors }
}

export const handleFetchResponse =
  <Data>(onSuccess?: (data: Data) => void, onFailure?: (errors?: IErrors) => void) =>
  (response: { data: Data } | { error: FetchBaseQueryError | SerializedError }) => {
    if ('error' in response && 'status' in response.error && Number.isInteger(response.error.status)) {
      const { errors } = collectApiErrors(response.error.data as { message: IApiMessage[] }, response.error.status)

      onFailure?.(errors)

      return errors
    }

    if ('data' in response) {
      onSuccess?.(response.data)
    }
  }

const baseQuery = fetchBaseQuery({
  baseUrl: getEnv('VITE_REACT_APP_API'),
  prepareHeaders: (headers) => {
    const token = getCookie({ name: 'token' })

    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }

    return headers
  }
})

export const baseQueryWithErrorHandling: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions: { isSilentOnFailure?: boolean; isSilentOnSuccess?: boolean }
) => {
  const result = await baseQuery(args, api, extraOptions)
  const serverVersion = result.meta?.response?.headers.get('x-client-version')
  const state = api.getState() as TypeRootState
  const { clientVersion } = state.version

  if (serverVersion) {
    api.dispatch(setServerVersion(serverVersion))

    if (!clientVersion) {
      api.dispatch(setClientVersion(serverVersion))
    }
  }

  const cleanAuthData = () => {
    deleteCookie({ name: 'token' })
    deleteCookie({ name: 'refreshToken' })
    deleteCookie({ name: 'validUntil' })
    api.dispatch(authActions.resetAuthStore())
  }

  if (result.error && 'status' in result.error && result.error.status === 401) {
    cleanAuthData()
  }

  if (result.error && 'data' in result.error && result.error.status) {
    const hasRedirect = checkRedirect(result.error.status, result.meta?.request.url)

    if (hasRedirect) {
      return result
    }

    const errorData = result.error?.data as { message: IApiMessage[] }
    const errorMessages = errorData.message
    const isSomeHasPath = errorMessages.some((error) => Boolean(error.path))

    if (!isSomeHasPath && !extraOptions?.isSilentOnFailure) {
      if (errorMessages && errorMessages.length) {
        const [errorMessage] = errorMessages
        notify({ type: 'error', title: errorMessage.title })
      } else {
        notify({ type: 'error', title: String(errorMessages) })
      }
    }
  }

  if (result.data && typeof result.data === 'object' && 'message' in result.data && !extraOptions?.isSilentOnSuccess) {
    const data = result.data as { message: IApiMessage[] }
    const messages = data.message
    const [message] = messages

    notify({ type: 'success', title: message.title })
  }

  return result
}
